import { FwFClient } from '@deliveryhero/fwf-sdk-javascript'
import { cloneDeep, debounce } from 'lodash'
import type { Writeable } from 'zod'
import {
  EXPIRATION_TIME_IN_MILLIS,
  REQUEST_TIMEOUT_IN_MILLIS,
  normalizeEvaluatedToggles,
} from '../../constants'
import { togglesDefinitions } from '../../definitions'
import type { TogglesDefinitions, TogglesUser, TogglesUserAttributes } from '../../types'
import { TogglesUpdatedEvent } from './toggles-updated-event'
import { getConfig } from '@/tools/config'

let togglesCache: Writeable<TogglesDefinitions> = cloneDeep(togglesDefinitions)
let togglesClient: FwFClient | null = null

/**
 *
 * Check if client is initialized
 *
 */
function isInitialized(): boolean {
  return Boolean(togglesClient)
}

/**
 *
 * Asserts if client is initialized
 *
 */
function assertIsInitialized(togglesClient: FwFClient | null): asserts togglesClient is FwFClient {
  if (!isInitialized()) {
    initToggles()
  }
}

/**
 *
 * Evaluate the toggles and propagate the changes to the application
 * by emitting a global custom event. (Only in browser)
 *
 */
const evaluateTogglesAndPropagate = async (): Promise<void> => {
  assertIsInitialized(togglesClient)

  const evaluatedToggles = await togglesClient.getVariations(
    Object.keys(togglesDefinitions),
    togglesCache
  )
  togglesCache = normalizeEvaluatedToggles(evaluatedToggles)
  document.dispatchEvent(new TogglesUpdatedEvent(togglesCache))
}

/**
 *
 * Initialize the toggles client
 * - Sets the initial NOT_AUTHENTICATED user
 * - Sync the toggles with the server, and sets up the callback method that will be
 *   executed when the toggles are updated. (only in browser)
 * - Performs initial toggles evaluation (only in browser)
 *
 */
export const initToggles = (toggles: TogglesDefinitions | null = null): void => {
  if (toggles) {
    togglesCache = toggles
  }
  if (isInitialized()) {
    return
  }

  const { isBrowser, funWithFlagsToken } = getConfig()

  togglesClient = new FwFClient({
    accessToken: funWithFlagsToken,
    featureKeys: isBrowser ? Object.keys(togglesDefinitions) : [],
    updateFeatures: isBrowser,
    featuresExpirationTime: EXPIRATION_TIME_IN_MILLIS,
    requestTimeout: REQUEST_TIMEOUT_IN_MILLIS,
  })

  if (isBrowser) {
    togglesClient
      .sync()
      .then(async () => {
        togglesClient!.setRenderer(evaluateTogglesAndPropagate)
        await evaluateTogglesAndPropagate()
      })
      .catch((e) => {
        throw e
      })
  }
}

/**
 *
 * Set the user id and email on the toggles client if provided. If null is passed will instead set the
 * unAuthUserId. In both cases, once set, will remove all existing user attributes.
 * Note: it triggers local re-evaluation all the toggles (only in browser)
 *
 */
export const setUser = (user: TogglesUser | null): void => {
  assertIsInitialized(togglesClient)

  const togglesUser = togglesClient.getUser()
  if (user) {
    togglesUser.authUserId = user.id
    togglesUser.email = user.email
    togglesUser.unAuthUserId = ''
  } else {
    togglesUser.unAuthUserId = 'NOT_AUTHENTICATED'
    togglesUser.authUserId = ''
    togglesUser.email = ''
    removeUserAttributes()
  }
}

/**
 *
 * Set the user attributes on the toggles client.
 * Once set, re-evaluates locally all the toggles (only in browser)
 *
 */
export const setTogglesAttributes = debounce((userAttributes: TogglesUserAttributes): void => {
  assertIsInitialized(togglesClient)

  const { isBrowser } = getConfig()
  togglesClient.getUser().setAttributes(userAttributes)
  if (isBrowser) {
    void evaluateTogglesAndPropagate()
  }
}, 300)

export const getUserAttributes = (): Record<string, unknown> => {
  assertIsInitialized(togglesClient)
  const user = togglesClient.getUser()
  return user.getAttributes()
}

/**
 *
 * Remove al user attributes from the toggles client
 * Once set, re-evaluates locally all the toggles (only in browser)
 *
 */
export const removeUserAttributes = (): void => {
  assertIsInitialized(togglesClient)

  const { isBrowser } = getConfig()
  const user = togglesClient.getUser()
  const attributes = user.getAttributes()
  for (const attribute in attributes) {
    try {
      user.removeAttribute(attribute)
      // eslint-disable-next-line @glovo/custom-rules/always-forward-error
    } catch (_e) {
      // NO-OP (for now)
      // Ticket : https://glovoapp.atlassian.net/browse/WEBX-757
    }
  }

  if (isBrowser) {
    void evaluateTogglesAndPropagate()
  }
}

/**
 *
 * Get the toggles from the cache, which contains the latest toggles local evaluation.
 *
 */
export const getToggles = (): TogglesDefinitions => togglesCache
